import type { ImageMetaProps } from '~/server/schema/image.schema';
import { decodeUserComment } from '~/utils/encoding-helpers';
import type { SDResource } from '~/utils/metadata/base.metadata';
import { createMetadataProcessor } from '~/utils/metadata/base.metadata';
import { parseAIR } from '~/utils/string-helpers';

type CivitaiResource = {
  weight?: number;
  air?: string;
  modelVersionId?: number;
  type?: string;
  versionName?: string;
  modelName?: string;
};

// #region [helpers]
const hashesPrefix = ', Hashes: ';
const civitaiResources = /, Civitai resources:\s*(\[\{.*?\}\])/;
const civitaiMetadataPrefix = ', Civitai metadata: ';
const badExtensionKeys = ['Resources: ', 'Hashed prompt: ', 'Hashed Negative prompt: '];
const templateKeys = ['Template: ', 'Negative Template: '] as const;
const automaticExtraNetsRegex = /<(lora|hypernet):([a-zA-Z0-9_\.\-]+):([0-9.]+)>/g;
const automaticNameHash = /([a-zA-Z0-9_\.]+)\(([a-zA-Z0-9]+)\)/;
const automaticSDKeyMap = new Map<string, string>([
  ['Seed', 'seed'],
  ['CFG scale', 'cfgScale'],
  ['Sampler', 'sampler'],
  ['Steps', 'steps'],
  ['Clip skip', 'clipSkip'],
]);
const getSDKey = (key: string) => automaticSDKeyMap.get(key.trim()) ?? key.trim();

const automaticSDEncodeMap = new Map<keyof ImageMetaProps, string>(
  Array.from(automaticSDKeyMap, (a) => a.reverse()) as Iterable<readonly [string, string]>
);
const excludedKeys = [
  'hashes',
  'civitaiResources',
  'scheduler',
  'vaes',
  'additionalResources',
  'comfy',
  'upscalers',
  'models',
  'controlNets',
  'denoise',
  'other',
  'external',
];
/** Extract a balanced JSON object from a string, handling nested braces. */
function extractBalancedJson(
  str: string,
  prefix: string
): { json: string; start: number; end: number } | null {
  const prefixIndex = str.indexOf(prefix);
  if (prefixIndex === -1) return null;

  const jsonStart = str.indexOf('{', prefixIndex + prefix.length);
  if (jsonStart === -1) return null;

  let depth = 0;
  let inString = false;
  let escape = false;

  for (let i = jsonStart; i < str.length; i++) {
    const char = str[i];
    if (escape) {
      escape = false;
      continue;
    }
    if (char === '\\') {
      escape = true;
      continue;
    }
    if (char === '"') {
      inString = !inString;
      continue;
    }
    if (inString) continue;
    if (char === '{') depth++;
    else if (char === '}') {
      depth--;
      if (depth === 0) {
        return { json: str.substring(jsonStart, i + 1), start: prefixIndex, end: i + 1 };
      }
    }
  }

  return null;
}

function isPartialDate(date: string) {
  return date.length === 14 && date[11] === 'T';
}
function parseDetailsLine(line: string | undefined): Record<string, any> {
  const result: Record<string, any> = {};
  if (!line) return result;
  let currentKey = '';
  let currentValue = '';
  let insideQuotes = false;
  let insideDate = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];

    if (char === '"') {
      if (insideQuotes) {
        result[currentKey] = parseDetailsLine(currentValue.trim());
        currentKey = '';
      }
      insideQuotes = !insideQuotes;
    } else if (char === ':' && !insideQuotes && !insideDate) {
      if (isPartialDate(currentValue)) insideDate = true;
      else {
        currentKey = getSDKey(currentValue.trim());
        currentValue = '';
      }
    } else if (char === ',' && !insideQuotes) {
      if (insideDate) insideDate = false;
      if (currentKey) result[currentKey] = currentValue.trim();
      currentKey = '';
      currentValue = '';
    } else {
      currentValue += char;
    }
  }
  if (currentKey) result[currentKey] = currentValue.trim();

  return result;
}

export function normalizeGenerationDetails(details: string): string {
  if (!details) return '';
  const clean = details.replace(/^Parameters\s*:\s*/, '');

  // If a "Steps:" line already exists, the metadata is already structured across lines —
  // leave it untouched. This is what protects keywords that appear inside prompt text
  // (e.g. "Hires steps:", or a prompt that literally says "steps:"). Only jammed
  // single-line formats need fixing below.
  if (/(^|\n)Steps: ?\d/.test(clean)) return clean;

  // Put each section keyword on its own line. Split only when preceded by a real `,`/`.`
  // delimiter (what the single-line formats use as separators). Keep every quantifier
  // bounded ({0,7}, not *) so a crafted run of delimiters can't cause catastrophic
  // backtracking (ReDoS) — this runs on untrusted uploaded image metadata.
  return clean
    .replace(/[ \t\r]{0,7}[.,][ \t\r.,]{0,7}(Negative prompt:)/gi, '\n$1')
    .replace(/[ \t\r]{0,7}[.,][ \t\r.,]{0,7}(Steps:)(?=\s*\d)/gi, '\n$1');
}
// #endregion

export const automaticMetadataProcessor = createMetadataProcessor({
  canParse(exif) {
    let generationDetails = null;
    if (exif?.parameters) {
      generationDetails = exif.parameters;
    } else if (exif?.userComment) {
      generationDetails = decodeUserComment(exif.userComment);
    }

    if (generationDetails?.includes('Steps: ')) {
      exif.generationDetails = normalizeGenerationDetails(generationDetails);
      return true;
    }

    return false;
  },
  parse(exif) {
    const metadata: ImageMetaProps = {};
    const generationDetails = exif.generationDetails as string;

    if (!generationDetails) return metadata;
    const normalizedDetails = normalizeGenerationDetails(generationDetails);
    const metaLines = normalizedDetails.split('\n').filter((line) => line.trim() !== '');

    // Remove templates
    for (const key of templateKeys) {
      const templateLineIndex = metaLines.findIndex((line) => line.startsWith(key));
      if (templateLineIndex === -1) continue;
      metaLines.splice(templateLineIndex, 1);

      // Remove all lines until we hit a new key `[\w\s]+: `
      while (
        templateLineIndex < metaLines.length &&
        !/[\w\s]+: /.test(metaLines[templateLineIndex])
      ) {
        metaLines.splice(templateLineIndex, 1);
      }
    }

    let detailsLine = metaLines.find((line) => line.startsWith('Steps: '))?.replace(/\,\s*$/, '');
    // Strip it from the meta lines
    if (detailsLine) metaLines.splice(metaLines.indexOf(detailsLine), 1);
    // Remove meta keys I wish I hadn't made... :(
    for (const key of badExtensionKeys) {
      if (!detailsLine?.includes(key)) continue;
      detailsLine = detailsLine.split(key)[0];
    }

    // Extract Hashes
    const hashesResult = detailsLine ? extractBalancedJson(detailsLine, hashesPrefix) : null;
    if (hashesResult && detailsLine) {
      metadata.hashes = JSON.parse(hashesResult.json);
      detailsLine = detailsLine.slice(0, hashesResult.start) + detailsLine.slice(hashesResult.end);
    }

    // Extract Civitai Resources
    const civitaiResourcesMatch = detailsLine?.match(civitaiResources)?.[1];
    if (civitaiResourcesMatch && detailsLine) {
      metadata.civitaiResources = JSON.parse(civitaiResourcesMatch);
      for (const resource of metadata.civitaiResources as CivitaiResource[]) {
        delete resource.modelName;
        delete resource.versionName;
        if (!resource.air) continue;
        const { version, type } = parseAIR(resource.air);
        resource.modelVersionId = version;
        resource.type = type;
        delete resource.air;
      }
      detailsLine = detailsLine.replace(civitaiResources, '');
    }

    // Extract Civitai Metadata (uses balanced brace extraction to handle nested JSON)
    const civitaiMetadataMatch = detailsLine
      ? extractBalancedJson(detailsLine, civitaiMetadataPrefix)
      : null;
    if (civitaiMetadataMatch && detailsLine) {
      const data = JSON.parse(civitaiMetadataMatch.json) as Record<string, any>;
      if (Object.keys(data).length !== 0) metadata.extra = data;
      detailsLine =
        detailsLine.slice(0, civitaiMetadataMatch.start) +
        detailsLine.slice(civitaiMetadataMatch.end);
    }

    // Extract fine details
    const details = parseDetailsLine(detailsLine);
    for (const [k, v] of Object.entries(details)) {
      const key = automaticSDKeyMap.get(k) ?? k;
      if (excludedKeys.includes(key)) continue;
      metadata[key] = v;
    }

    // Extract prompts
    const [prompt, ...negativePrompt] = metaLines
      .join('\n')
      .split('Negative prompt:')
      .map((x) => x.trim());
    metadata.prompt = prompt;
    metadata.negativePrompt = negativePrompt.join(' ').trim();

    // Extract resources
    const extranets = [...prompt.matchAll(automaticExtraNetsRegex)];
    const resources: SDResource[] = extranets.map(([, type, name, weight]) => ({
      type,
      name,
      weight: parseFloat(weight),
    }));

    // Extract Lora hashes
    if (metadata['Lora hashes']) {
      if (!metadata.hashes) metadata.hashes = {};
      for (const [name, hash] of Object.entries(metadata['Lora hashes'])) {
        metadata.hashes[`lora:${name}`] = hash;
        const resource = resources.find((r) => r.name === name);
        if (resource) resource.hash = hash;
        else resources.push({ type: 'lora', name, hash });
      }
      delete metadata['Lora hashes'];
    }

    // Extract VAE
    if (metadata['VAE hash']) {
      if (!metadata.hashes) metadata.hashes = {};
      metadata.hashes['vae'] = metadata['VAE hash'] as string;
      delete metadata['VAE hash'];
    }

    // Extract Model hash
    if (metadata['Model'] && metadata['Model hash']) {
      if (!metadata.hashes) metadata.hashes = {};
      if (!metadata.hashes['model']) metadata.hashes['model'] = metadata['Model hash'] as string;

      resources.push({
        type: 'model',
        name: metadata['Model'] as string,
        hash: metadata['Model hash'] as string,
      });
    }

    // Extract Refiner hash
    if (metadata['Refiner'] && metadata['Refiner hash']) {
      if (!metadata.hashes) metadata.hashes = {};
      if (!metadata.hashes['refiner'])
        metadata.hashes['refiner'] = metadata['Refiner hash'] as string;

      resources.push({
        type: 'model',
        name: (metadata['Refiner'] as string).replace(/\.[^/.]+$/, ''),
        hash: metadata['Refiner hash'] as string,
      });
    }

    // Extract Size into width/height
    if (metadata['Size'] && typeof metadata['Size'] === 'string') {
      const [w, h] = (metadata['Size'] as string).split('x').map(Number);
      if (w && h) {
        metadata.width = w;
        metadata.height = h;
      }
      delete metadata['Size'];
    }

    // Extract hypernetwork details
    if (metadata['Hypernet'] && metadata['Hypernet strength'])
      resources.push({
        type: 'hypernet',
        name: metadata['Hypernet'] as string,
        weight: parseFloat(metadata['Hypernet strength'] as string),
      });

    if (metadata['AddNet Enabled'] === 'True') {
      let i = 1;
      while (true) {
        const fullname = metadata[`AddNet Model ${i}`] as string;
        if (!fullname) break;
        const [, name, hash] = fullname.match(automaticNameHash) ?? [];

        // The extension writes `AddNet Weight A ${i}` (unet) / `B ${i}` (text encoder).
        // A NaN weight fails imageMetaSchema validation, which discards the ENTIRE
        // metadata object — so a missing key must yield no weight, never NaN.
        const weight = parseFloat(
          (metadata[`AddNet Weight A ${i}`] ?? metadata[`AddNet Weight ${i}`]) as string
        );
        resources.push({
          type: (metadata[`AddNet Module ${i}`] as string).toLowerCase(),
          name,
          hash,
          weight: Number.isFinite(weight) ? weight : undefined,
        });
        i++;
      }
    }

    metadata.resources = resources;

    return metadata;
  },
  encode({ prompt, negativePrompt, resources, steps, ...other }) {
    const lines = [prompt];
    if (negativePrompt) lines.push(`Negative prompt: ${negativePrompt}`);
    const fineDetails = [];
    if (steps) fineDetails.push(`Steps: ${steps}`);
    for (const [k, v] of Object.entries(other)) {
      if (v == null || typeof v === 'object') continue;
      const key = automaticSDEncodeMap.get(k) ?? k;
      if (excludedKeys.includes(key)) continue;
      fineDetails.push(`${key}: ${v}`);
    }
    if (fineDetails.length > 0) lines.push(fineDetails.join(', '));

    return lines.join('\n');
  },
});
